// @atlas/contracts — a `accounts` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { ACCOUNT_DOMAIN_STATUSES, domainKey, normalizeDomainToken, INTEGRATION_EXTERNAL_ID_MAX, INTEGRATION_NOTE_MAX, INTEGRATION_SYSTEMS, INTEGRATIONS_MAX, } from '@atlas/shared';
import { z } from 'zod';
import { AccountNamedRefSchema, AccountPersonRefSchema, ClientBillingProfileSchema, ClientStatusSchema, accountsEmptyToNull, nullableStr, } from '../_shared/schemas.js';
// ── Foundation-primitívek (az Odin admin read-API-ból, az app-backenden át) ──
export const AccountSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    name: z.string(),
    status: z.string(),
    createdAt: z.string(),
});
/**
 * Gazdag account-sor (a régi CRM account-listájával adat-szintű parity). A platform-primitív (id/clientId/name)
 * a tenant-tő; a hospitality-specifikus mezők az Odin `clients/account` domain-resource-éből (agnosztikus JSON).
 * A `status` az EREDETI CRM-státusz (active/pending/inactive) — adat-parity, nem a platform-EntityStatus.
 */
/**
 * Egy fiók-integráció: a belső `id` (erre hivatkozik a hozzárendelés kötése) · a rendszer · a külső azonosító ·
 * a megjegyzés (pl. a HotelCMS-egység domainje). Egyediség szándékosan NINCS (user-döntés) — a dupla felvétel
 * felhasználói hiba. Az `id` a kliens által generált UUID (új elem), vagy a meglévő elemé (szerkesztés).
 */
export const AccountIntegrationSchema = z.object({
    id: z.string().uuid(),
    system: z.enum(INTEGRATION_SYSTEMS),
    externalId: z.string().trim().min(1).max(INTEGRATION_EXTERNAL_ID_MAX),
    note: z.preprocess(accountsEmptyToNull, z.string().trim().max(INTEGRATION_NOTE_MAX).nullable().default(null)),
});
/**
 * Az integráció-lista a write-body-kban: legfeljebb `INTEGRATIONS_MAX` elem, és egy `id` nem állhat kétszer (a
 * kötés `(fiók, id)` párra mutat — a dupla `id` nem eldönthető hivatkozás). A KÜLSŐ azonosító ismétlődhet.
 */
const IntegrationListSchema = z
    .array(AccountIntegrationSchema)
    .max(INTEGRATIONS_MAX)
    .refine((list) => new Set(list.map((i) => i.id)).size === list.length, {
    message: 'Egy integráció-azonosító kétszer szerepel',
});
export const AccountRowSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    name: z.string(),
    status: z.string(),
    website: z.string().nullable(),
    type: z.string().nullable(),
    category: z.string().nullable(),
    countryCode: z.string().nullable(),
    countryName: z.string().nullable(),
    zip: z.string().nullable(),
    city: z.string().nullable(),
    address: z.string().nullable(),
    note: z.string().nullable(),
    /**
     * A fiók (nem-fő) DOMAINJEI névsorban — a `clients/account-domains` élő, `isPrimary=false` soraiból. A lista
     * „Domainek" oszlopa és a keresés ebből olvas; a fő domain a `website`. (A szabad szöveges `otherDomains`
     * utódja: a domainek tételesen, állapottal élnek — a detail `accountDomains`-e hordozza a teljes alakot.)
     */
    domains: z.array(z.string()).default([]),
    bookingEngine: z.string().nullable(),
    pms: z.string().nullable(),
    channelManager: z.string().nullable(),
    emailWebHosting: z.string().nullable(),
    /** Régió KÓD (a label az Odin `reference/account-region`-jából, az accountRefs `region` szótárán át feloldva). */
    region: z.string().nullable(),
    /** Szobaszám (nem-negatív egész) — null, ha nincs megadva. */
    roomCount: z.number().int().nullable(),
    /** Több-választós Típus/kategória (a régi single `type` fejlődménye, backfillelve); az opció-forrás az accountRefs `type` szótára. */
    types: z.array(z.string()).default([]),
    /** A szülő-ügyfél egy meglévő számlázási profiljának azonosítója (CSAK az id; a profilt az Atlas oldja fel). */
    billingProfileId: z.string().nullable(),
    /**
     * A fiók SAJÁT számlázása (a legacy `accounts.override_billing`). KÉT hatása van, és egyik sem következik a
     * `billingProfileId` létéből:
     *   1. **effektív profil**: bekapcsolva a fiók saját profilja számláz, különben az ügyfél ELSŐDLEGES profilja,
     *   2. **számla-SZEPARÁCIÓ**: a fiók-szintű hozzárendelés kikapcsolva az ÜGYFÉL számlájába OLVAD BE,
     *      bekapcsolva a fiók önálló számlát kap (a legacy havi generátor csoportosító kulcsa).
     */
    overrideBilling: z.boolean(),
    /** A felülíró profil FELOLDOTT neve (a lista/detail megjelenítéséhez) — nincs/nem feloldható → null. */
    billingProfileName: z.string().nullable(),
    /** Integráció: az Asana-projekt/ügyfél neve (a `clients/account` v3 séma `asanaName` mezője) — null, ha nincs. */
    asanaName: z.string().nullable(),
    /**
     * Integráció: a fiók külső rendszer-azonosítói (HotelCMS · ZadirCMS · MailWizz), rendszerenként TÖBB is, mind
     * megjegyzéssel. A szolgáltatás-hozzárendelés ezek `id`-jára hivatkozik (`usageRefs`) — l. `@atlas/shared`
     * `account-integrations`. A régi egy-azonosítós mezőkből álló fiókon a lista determinisztikus azonosítóval áll elő.
     */
    integrations: z.array(AccountIntegrationSchema),
    // ── LEGACY-PARITÁS mezők (a `clients/account` v4 séma) ────────────────────────────────
    /**
     * Földrajzi koordináta — a forrásban `decimal(10,6)`, és **STRINGKÉNT** utazik: a string LOSSLESS
     * (nincs float-kerekítés), a 6 tizedes ~10 cm-es felbontás. A megjelenítés/térkép a UI dolga.
     */
    latitude: z.string().nullable(),
    longitude: z.string().nullable(),
    /** Cloudflare-en van-e a fiók (`accounts.cloudflare`). */
    cloudflare: z.boolean().default(false),
    /**
     * Automatikus (havi) számlázás a fiók szintjén (`accounts.auto_billing`). A legacy havi számlázó
     * account-scope-nál `auto_billing = true ÉS status = active` feltétellel válogat.
     */
    autoBilling: z.boolean().default(false),
    /** Export-kampány kulcsok (legacy `export_campaigns` JSON) — pl. `["export_en","export_de"]`. */
    exportCampaigns: z.array(z.string()).default([]),
    /** SMTP-szolgáltató kulcsok (legacy `smtps` JSON) — pl. `["postmark"]`. */
    smtps: z.array(z.string()).default([]),
    /** Levelezés-szolgáltató kulcsok (legacy `mailings` JSON) — pl. `["zadirmail","dns_google"]`. */
    mailings: z.array(z.string()).default([]),
    /** Ügyféladminisztrátorok: az `account_affiliations` kind='admin' kötődések, identity-re (név+avatar) feloldva. */
    admins: z.array(AccountPersonRefSchema),
    /** Kapcsolattartók: az `account_affiliations` kind='contact' kötődések, identity-re (név+avatar) feloldva. */
    contacts: z.array(AccountPersonRefSchema),
    /** Szolgáltatások: a fiók aktív service-plan-assignment-jei → szolgáltatás-névre feloldva (best-effort). */
    services: z.array(AccountNamedRefSchema),
});
/**
 * Egy fiók-kötés (affiliation) TELJES flag-alakja a fiók-detail szerkesztőjéhez (identity-re + kind-ra bontva).
 * Az `AccountRowSchema.admins/.contacts` (AccountPersonRef) olvasás-alak VÁLTOZATLAN marad (a lista is azt
 * használja, flag NÉLKÜL); ez a KÜLÖN mező adja a szerkesztőnek a jelenlegi isPrimary/portalAccess/managerType-ot
 * (a detail-válaszba ágyazva, csak a detail — a lista nem hordozza). A `kind` a kötés minősége.
 */
export const AccountAffiliationDetailSchema = z.object({
    identityId: z.string(),
    kind: z.enum(['admin', 'contact']),
    isPrimary: z.boolean(),
    portalAccess: z.boolean(),
    /** Menedzser-típus (csak admin-kötésnél) — vagy null. */
    managerType: z.string().nullable(),
});
/**
 * Fiók-detail: a sor + OCC-verzió (a `clients/account` resource `meta.version`-je, az edit If-Match forrása).
 * A read-only detail ÉS a szerkesztő ugyanezt olvassa; a lista (AccountRow) verzió nélkül marad (ott nincs edit).
 */
/** A domain állapota (aktív · setup alatt · inaktív) — az Odin registry-séma enumjával egyező készlet. */
export const AccountDomainStatusSchema = z.enum(ACCOUNT_DOMAIN_STATUSES);
/** A fiók egy domainje a „Domainek" szekcióhoz (a `clients/account-domains` sora; a fő domain nincs benne). */
export const AccountDomainSchema = z.object({
    id: z.string(),
    domain: z.string(),
    status: AccountDomainStatusSchema,
    /** OCC — a szerkesztés If-Match forrása. */
    version: z.number(),
});
/** A fiók-domainek felső korlátja egy mentésben (DoS-plafon; mérve a legtöbb egy fiókon: 17). */
export const ACCOUNT_DOMAINS_MAX = 100;
/**
 * Egy domain-sor a fiók MENTÉSÉBEN (a „Domainek" szekció ismételhető sora — a rendszerazonosítók mintájára, a
 * lap egyetlen Mentés-gombjával). `id` = a meglévő sor azonosítója (módosítás); hiányzik → új sor. A `domain` a
 * közös szabállyal normalizálódik (`https://Példa.HU/út` → `pelda.hu`-alak; a nem domain-alakú szöveg 422).
 */
export const AccountDomainItemSchema = z.object({
    id: z.string().min(1).max(64).optional(),
    domain: z
        .string()
        .trim()
        .min(1, 'A domain megadása kötelező')
        .max(2000)
        .transform((raw, ctx) => {
        const r = normalizeDomainToken(raw);
        if ('reason' in r) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Érvénytelen domain (pl. pelda.hu)' });
            return z.NEVER;
        }
        return r.domain;
    }),
    status: AccountDomainStatusSchema.default('active'),
});
/**
 * A mentett domain-lista a VÉGÁLLAPOT (nem patch): ami nincs benne, az a fiókról lekerül. Ugyanaz a domain
 * kétszer nem szerepelhet (a kanonikus punycode-kulcson hasonlítva — `domainKey`).
 */
const accountDomainsField = z
    .array(AccountDomainItemSchema)
    .max(ACCOUNT_DOMAINS_MAX)
    .superRefine((list, ctx) => {
    const seen = new Set();
    list.forEach((d, i) => {
        const key = domainKey(d.domain);
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Ez a domain kétszer szerepel',
                path: [i, 'domain'],
            });
        }
        seen.add(key);
    });
})
    .optional();
export const AccountDetailSchema = AccountRowSchema.extend({
    version: z.number(),
    /** A fiók kötéseinek TELJES flag-alakja (a „Kapcsolatok" szerkesztőhöz) — a lista-alak (admins/contacts) mellett. */
    affiliationDetails: z.array(AccountAffiliationDetailSchema).default([]),
    /**
     * A szülő-ügyfél számlázási profiljai a billing-profil-választóhoz (id + cégnév/adószám/cím). A foundation
     * `/v1/admin/clients` SZÁMLÁZÁSI-IDENTITÁS projekciója (SEMMI összeg/kártya) — a fiók `clientId`-jére szűrve.
     * Best-effort: nincs jog / nincs profil → üres. (A detailbe ágyazva — a services/admins/contacts mintájára.)
     */
    billingProfileOptions: z.array(ClientBillingProfileSchema).default([]),
    /** A fiók (nem-fő) domainjei a „Domainek" szekcióhoz — teljes alakban (állapot + OCC-verzió), névsorban. */
    accountDomains: z.array(AccountDomainSchema).default([]),
});
/**
 * Koordináta-mező: üres → null; egyébként `decimal(10,6)`-alakú STRING (opcionális előjel, max 6 tizedes).
 * SZÁNDÉKOSAN string és nem `number`: a string LOSSLESS (nincs float-kerekítés), és a szerződés a
 * `clients/account` v4 sémájával egyezik. A tartomány is ellenőrzött (a lat/lng fizikai határai).
 */
const nullableCoordinate = z.preprocess(accountsEmptyToNull, z
    .string()
    .regex(/^-?\d{1,3}(\.\d{1,6})?$/, 'Érvénytelen koordináta (max 6 tizedesjegy)')
    .refine((v) => Math.abs(Number(v)) <= 180, 'A koordináta a ±180 tartományon kívül van')
    .nullable()
    .optional());
/** Szobaszám-mező: üres/null/undefined → null, egyébként nem-negatív egész (nullable, opcionális). */
const nullableRoomCount = z
    .preprocess((v) => (v === '' || v === null || v === undefined ? null : v), z.number().int().nonnegative().nullable())
    .optional();
export const AccountUpdateBodySchema = z.object({
    name: z.string().trim().min(1).max(255).optional(),
    status: ClientStatusSchema.optional(),
    website: nullableStr(255),
    type: nullableStr(64),
    category: nullableStr(16),
    countryCode: nullableStr(8),
    countryName: nullableStr(128),
    zip: nullableStr(32),
    city: nullableStr(128),
    address: nullableStr(255),
    note: nullableStr(4000),
    bookingEngine: nullableStr(64),
    pms: nullableStr(64),
    channelManager: nullableStr(64),
    emailWebHosting: nullableStr(64),
    /** Régió KÓD (nullable) — a label az accountRefs `region` szótárából oldódik fel. */
    region: nullableStr(64),
    /** Szobaszám (nem-negatív egész vagy null). */
    roomCount: nullableRoomCount,
    /** Több-választós Típus/kategória (a UI ezt szerkeszti a régi single `type` helyett).
     *  Korlátozott (defense-in-depth): legfeljebb 20 elem, elemenként max 64 karakter. */
    types: z.array(z.string().max(64)).max(20).optional(),
    /** A szülő-ügyfél egy számlázási profiljának id-ja (CSAK az id) vagy null. */
    billingProfileId: nullableStr(64),
    /** A fiók saját számlázása (a legacy `override_billing`) — lásd az `AccountRowSchema` magyarázatát. */
    overrideBilling: z.boolean().optional(),
    /** Az Asana-projekt/ügyfél neve — üres → null. */
    asanaName: nullableStr(255),
    /** A „Domainek" szekció VÉGÁLLAPOTA (hiányzik → a domainek érintetlenek). */
    domains: accountDomainsField,
    /**
     * A külső rendszer-azonosítók TELJES listája (nem patch: a küldött lista a teljes új állapot). Hiányzik →
     * érintetlen. A BFF a rendszerenkénti első elemet a régi egy-azonosítós mezőkbe is tükrözi.
     */
    integrations: IntegrationListSchema.optional(),
    // ── LEGACY-PARITÁS mezők (v4). A koordináta STRING (lossless decimal); a flagek boolean; a kulcs-listák
    //    string-tömbök, elem-hosszal és darabszámmal KORLÁTOZVA (defense-in-depth a séma előtt).
    latitude: nullableCoordinate,
    longitude: nullableCoordinate,
    cloudflare: z.boolean().optional(),
    autoBilling: z.boolean().optional(),
    exportCampaigns: z.array(z.string().max(64)).max(50).optional(),
    smtps: z.array(z.string().max(64)).max(50).optional(),
    mailings: z.array(z.string().max(64)).max(50).optional(),
});
/**
 * Fiók-létrehozás body — az Odin foundation atomi `POST /v1/admin/accounts`-hoz. A `clientId` a szülő-ügyfél
 * UUID-je (a user választja — a szülőre az Odin authz + a user jog-scope-ja hat; NEM tenant-célzás-hamisítás).
 * A `status` a CRM-státusz (a `clients/account` profil `data.status` mezőjébe kerül; a foundation-root
 * platform-státusza default `active` — a kliens-create mintája). A többi mező a `clients/account` v2 séma
 * WRITABLE kulcsa (`additionalProperties:false`). A régi single `type` NINCS a body-ban (a UI a `types`-t
 * szerkeszti); a szerver séma-kompatból megtűrné, de a BFF nem küldi. Üres string → null (mező „nincs érték").
 */
export const AccountCreateBodySchema = z.object({
    clientId: z.string().uuid(),
    name: z.string().trim().min(1).max(255),
    status: ClientStatusSchema.optional(),
    website: nullableStr(255),
    /** Korlátozott (defense-in-depth): legfeljebb 20 elem, elemenként max 64 karakter. */
    types: z.array(z.string().max(64)).max(20).default([]),
    category: nullableStr(16),
    countryCode: nullableStr(8),
    countryName: nullableStr(128),
    zip: nullableStr(32),
    city: nullableStr(128),
    address: nullableStr(255),
    note: nullableStr(4000),
    bookingEngine: nullableStr(64),
    pms: nullableStr(64),
    channelManager: nullableStr(64),
    emailWebHosting: nullableStr(64),
    region: nullableStr(64),
    roomCount: nullableRoomCount,
    billingProfileId: nullableStr(64),
    /** A fiók saját számlázása (a legacy `override_billing`) — lásd az `AccountRowSchema` magyarázatát. */
    overrideBilling: z.boolean().optional(),
    /** Az Asana-projekt/ügyfél neve — üres → null. */
    asanaName: nullableStr(255),
    /** A „Domainek" szekció VÉGÁLLAPOTA (hiányzik → a domainek érintetlenek). */
    domains: accountDomainsField,
    /**
     * A külső rendszer-azonosítók TELJES listája (nem patch: a küldött lista a teljes új állapot). Hiányzik →
     * érintetlen. A BFF a rendszerenkénti első elemet a régi egy-azonosítós mezőkbe is tükrözi.
     */
    integrations: IntegrationListSchema.optional(),
    // ── LEGACY-PARITÁS mezők (v4). A koordináta STRING (lossless decimal); a flagek boolean; a kulcs-listák
    //    string-tömbök, elem-hosszal és darabszámmal KORLÁTOZVA (defense-in-depth a séma előtt).
    latitude: nullableCoordinate,
    longitude: nullableCoordinate,
    cloudflare: z.boolean().optional(),
    autoBilling: z.boolean().optional(),
    exportCampaigns: z.array(z.string().max(64)).max(50).optional(),
    smtps: z.array(z.string().max(64)).max(50).optional(),
    mailings: z.array(z.string().max(64)).max(50).optional(),
});
/** Fiók-létrehozás válasza: a friss id + OCC-verzió (=0) + createdAt (a lista-invalidáláshoz/navigációhoz elég). */
export const AccountCreateResponseSchema = z.object({
    id: z.string(),
    version: z.number(),
    createdAt: z.string(),
});
/** Account-lista lekérdezés: szabad-szavas kereső, oszlop-szűrők, rendezés, offset-lapozás. */
export const AccountListQuerySchema = z.object({
    q: z.string().optional(),
    type: z.string().optional(),
    category: z.string().optional(),
    status: z.string().optional(),
    city: z.string().optional(),
    /** Szolgáltatás-facet: egy szolgáltatás id-ja → csak az azt (aktívan) használó fiókok. */
    service: z.string().optional(),
    clientId: z.string().optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
/** A fiók-kötés minősége: `admin` (ügyféladminisztrátor — belső kolléga) vagy `contact` (kapcsolattartó — partner). */
export const AccountAffiliationKindSchema = z.enum(['admin', 'contact']);
/** A menedzser-típus (ügyféladminisztrátornál): digitális marketinges vagy revenue manager — vagy null. */
export const AccountManagerTypeSchema = z.enum(['digital_marketing', 'revenue_management']);
/**
 * Fiók-oldali kötés (affiliation) UPSERT body: egy LÉTEZŐ identity (a `GET /identities` pickerből) kötése a
 * fiókhoz adott minőségben (`kind`). Az `accountId` az útvonalból (`:id`) jön; a cél-tenant (`clientId`) SOHA
 * nem kliens-input — a szerver a fiókból oldja fel (tenant-izoláció). Az `isPrimary` → a szerver kizárólagossá
 * teszi a fiókon a kind-on belül (a másik elsődlegest demótálja, OCC) + auto portál-hozzáférést nyit. A
 * `managerType` csak `kind='admin'`-nál értelmezett (kapcsolattartónál a szerver figyelmen kívül hagyja).
 */
export const AccountAffiliationUpsertBodySchema = z.object({
    /** A kötendő LÉTEZŐ identity id-ja (kind=admin → belső kolléga; kind=contact → partner). */
    identityId: z.string().uuid(),
    kind: AccountAffiliationKindSchema,
    /** Elsődleges a fiókon (a kind-on belül) — a szerver kizárólagossá teszi + portált nyit. */
    isPrimary: z.boolean().default(false),
    /** Ügyfél-portál hozzáférés ezen a kötésen (elsődlegesnél a szerver auto true-ra emeli). */
    portalAccess: z.boolean().default(false),
    /** Menedzser-típus (csak admin-kötésnél) — vagy null. */
    managerType: AccountManagerTypeSchema.nullable().default(null),
});
//# sourceMappingURL=schemas.js.map